import clsx from "clsx";
import { useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { Spinner } from "@/components/ui/Spinner";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import type { MaListItem, MaPositionPreview } from "@/lib/material";
import {
  FLOATING_TABLE_HEADER_Z_CLASS,
  useFloatingStickyHeader,
} from "@/pages/projekte/useFloatingProjectTableHeader";
import {
  fmtMaDateOnly,
  fmtMaDateTime,
  formatRequestAge,
  lieferterminTone,
  maAuftragLabel,
  maBearbeiterDisplayName,
  maIsOpen,
  maIsUrgentUnclaimedFix,
  maLieferKwEndIso,
  maLieferKwLabel,
  MaFortschrittBadge,
  maProjectLine,
  type LieferterminTone,
} from "@/pages/material/maListDisplay";

export interface MaListSectionProps {
  items: MaListItem[];
  loading?: boolean;
  onOpen: (id: number) => void;
}

const META_HEAD_COLUMNS = [
  "Nr.",
  "Eingang",
  "Liefertermin",
  "Stand",
  "Bearbeiter",
  "Verknüpfung",
] as const;

export function MaListSection({ items, loading, onOpen }: MaListSectionProps) {
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const shellRef = useRef<HTMLDivElement>(null);
  const headRef = useRef<HTMLDivElement>(null);
  const getHeaderEl = useCallback(() => headRef.current, []);

  const floating = useFloatingStickyHeader(
    shellRef,
    shellRef,
    getHeaderEl,
    {
      enabled: isDesktop && items.length > 0,
      syncKey: String(items.length),
    },
  );

  if (loading && items.length === 0) {
    return (
      <div className="flex items-center justify-center rounded-ios border border-separator bg-card py-12 text-label-secondary shadow-ios">
        <Spinner />
        <span className="ml-2 text-sm">Lädt …</span>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="rounded-ios border border-dashed border-separator-opaque bg-card p-8 text-center text-sm text-label-secondary shadow-ios">
        Keine Materialanforderungen gefunden.
      </div>
    );
  }

  return (
    <>
      <div
        ref={shellRef}
        className="ma-list-shell overflow-hidden rounded-ios border border-separator bg-card shadow-ios"
      >
        <div ref={headRef} className="ma-list-meta-head" aria-hidden={Boolean(floating)}>
          <MaListMetaHead />
        </div>

        <ul className="eh-orders-card-list ma-list-cards space-y-3 p-3 md:space-y-3 md:px-0 md:pb-3 md:pt-0" role="list">
          {items.map((ma, index) => (
            <li key={ma.id}>
              <MaCard
                ma={ma}
                stripe={index % 2 === 0 ? "even" : "odd"}
                onOpen={() => onOpen(ma.id)}
              />
            </li>
          ))}
        </ul>
      </div>

      {floating &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className={`pointer-events-none fixed ${FLOATING_TABLE_HEADER_Z_CLASS} overflow-hidden rounded-b-ios border border-t-0 border-separator bg-card-elevated shadow-[0_8px_18px_rgba(15,23,42,0.22)]`}
            style={{
              top: floating.top,
              left: floating.left,
              width: floating.width,
            }}
            aria-hidden
          >
            <div
              className="pointer-events-auto"
              style={{
                width: floating.tableWidth,
                transform: `translateX(${-floating.scrollLeft}px)`,
              }}
            >
              <div className="ma-list-meta-head ma-list-meta-head--floating">
                <MaListMetaHead />
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

function MaListMetaHead() {
  return (
    <>
      {META_HEAD_COLUMNS.map((label) => (
        <span key={label}>{label}</span>
      ))}
    </>
  );
}

function MaCard({
  ma,
  stripe,
  onOpen,
}: {
  ma: MaListItem;
  stripe: "even" | "odd";
  onOpen: () => void;
}) {
  const projectLine = maProjectLine(ma);
  const preview = Array.isArray(ma.position_preview) ? ma.position_preview : [];
  const monteurNote = (ma.notes || "").trim();
  const kwLabel = maLieferKwLabel(ma);
  const tone = lieferterminTone(
    ma.liefertermin,
    ma.status,
    maLieferKwEndIso(ma),
  );
  const open = maIsOpen(ma.status);
  const urgentFix = maIsUrgentUnclaimedFix(ma);

  return (
    <article
      className={clsx(
        "eh-order-card eh-order-card--clickable overflow-hidden rounded-ios shadow-ios transition",
        stripe === "even" ? "eh-order-card--even" : "eh-order-card--odd",
        urgentFix && "eh-order-card--urgent-fix",
      )}
      aria-labelledby={`ma-${ma.id}-title`}
    >
      <button type="button" className="eh-order-card__open block w-full text-left" onClick={onOpen}>
        <header className="eh-order-card__header ma-list-card-header">
          <div className="ma-list-meta-grid">
            <div className="ma-list-meta-cell min-w-0">
              <h3 id={`ma-${ma.id}-title`} className="eh-order-card__tk-row m-0">
                <span className="eh-order-tk-badge font-mono text-xs font-semibold">
                  {ma.ma_nummer}
                </span>
                {urgentFix ? (
                  <span className="ma-urgent-fix-pill ml-2 align-middle">Sofort</span>
                ) : null}
              </h3>
            </div>

            <div className="ma-list-meta-cell">
              <div className="text-xs">
                <div
                  className={clsx(
                    "font-semibold tabular-nums",
                    urgentFix
                      ? "text-status-red"
                      : open
                        ? "text-status-orange"
                        : "text-label-secondary",
                  )}
                >
                  {formatRequestAge(ma.created_at)}
                </div>
                <time className="mt-0.5 block whitespace-nowrap tabular-nums text-label-tertiary">
                  {fmtMaDateTime(ma.created_at)}
                </time>
              </div>
            </div>

            <div className="ma-list-meta-cell">
              <LieferterminBadge
                liefertermin={ma.liefertermin}
                kwLabel={kwLabel}
                fix={ma.liefertermin_fix}
                tone={tone}
                urgent={urgentFix}
              />
            </div>

            <div className="ma-list-meta-cell">
              <MaFortschrittBadge f={ma.monteur_fortschritt} status={ma.status} />
            </div>

            <div className="ma-list-meta-cell min-w-0">
              <BearbeiterCell ma={ma} />
            </div>

            <div className="ma-list-meta-cell min-w-0">
              <VerknuepfungCell ma={ma} />
            </div>
          </div>

          <div className="ma-list-auftrag-row min-w-0">
            <p className="font-medium leading-snug text-label">
              {projectLine || maAuftragLabel(ma)}
            </p>
            <p className="mt-0.5 text-xs text-label-secondary">
              {ma.created_by}
              <span className="text-label-tertiary"> · </span>
              {ma.position_count || 0} Pos.
              {(ma.attachment_count || 0) > 0 ? (
                <>
                  <span className="text-label-tertiary"> · </span>
                  {ma.attachment_count}{" "}
                  {(ma.attachment_count || 0) === 1 ? "Anhang" : "Anhänge"}
                </>
              ) : null}
            </p>
          </div>
        </header>

        <section className="eh-order-card__articles" aria-label="Materialpositionen">
          {monteurNote ? (
            <p className="ma-monteur-note whitespace-pre-wrap text-sm text-label-secondary">
              {monteurNote}
            </p>
          ) : null}
          <div className="eh-order-card__items">
            <p className="eh-order-card__items-label">Material</p>
            <MaterialPreview preview={preview} total={ma.position_count || 0} />
          </div>
        </section>
      </button>
    </article>
  );
}

function BearbeiterCell({ ma }: { ma: MaListItem }) {
  const name = maBearbeiterDisplayName(ma);
  if (!name) {
    return <span className="text-sm text-label-tertiary">—</span>;
  }
  return (
    <div className="space-y-0.5">
      <span className="text-sm font-medium text-label">{name}</span>
      {ma.bearbeitet_at ? (
        <span className="block text-xs tabular-nums text-label-tertiary">
          {fmtMaDateTime(ma.bearbeitet_at)}
        </span>
      ) : null}
    </div>
  );
}

function VerknuepfungCell({ ma }: { ma: MaListItem }) {
  const tkItems =
    Array.isArray(ma.linked_tk) && ma.linked_tk.length > 0
      ? ma.linked_tk
      : (ma.linked_bestellnummern || []).filter(Boolean).map((bestellnummer, idx) => ({
          order_id: idx,
          bestellnummer,
          cancelled_at: null as string | null,
        }));
  const taList = (ma.linked_ta_subjects || []).filter(Boolean);
  const aga = ma.linked_aga;
  const hasLinks =
    tkItems.length > 0 ||
    taList.length > 0 ||
    ma.has_anfrage ||
    ma.has_order ||
    ma.has_aga ||
    !!aga;

  if (!hasLinks) {
    return <span className="text-sm text-label-tertiary">—</span>;
  }

  return (
    <div className="flex flex-row flex-wrap items-center gap-1.5 min-w-0 w-full">
      {aga ? (
        <span
          role="link"
          tabIndex={0}
          className="max-w-full cursor-pointer truncate rounded-md border border-accent/35 bg-accent/10 px-2 py-0.5 text-xs font-medium text-accent hover:underline"
          title={`Angebotsanfrage ${aga.aga_nummer}`}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            window.location.assign(`/angebotsanfragen?id=${aga.aga_id}`);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.stopPropagation();
              e.preventDefault();
              window.location.assign(`/angebotsanfragen?id=${aga.aga_id}`);
            }
          }}
        >
          AGA · {aga.aga_nummer}
        </span>
      ) : ma.has_aga ? (
        <span className="rounded-md border border-accent/35 bg-accent/10 px-2 py-0.5 text-xs font-medium text-accent">
          AGA
        </span>
      ) : null}
      {tkItems.map((tk) => {
        const cancelled = Boolean(tk.cancelled_at);
        return (
          <span
            key={`${tk.order_id}-${tk.bestellnummer}`}
            className={clsx(
              "font-mono text-xs font-semibold max-w-full truncate",
              cancelled ? "eh-order-tk-badge eh-order-tk-badge--cancelled" : "eh-order-tk-badge",
            )}
            title={cancelled ? `${tk.bestellnummer} (storniert)` : tk.bestellnummer}
          >
            {tk.bestellnummer}
          </span>
        );
      })}
      {taList.map((ta) => (
        <span
          key={ta}
          className="max-w-full truncate rounded-md border border-accent/35 bg-accent/10 px-2 py-0.5 text-xs font-medium text-accent"
          title={ta}
        >
          TA · {ta}
        </span>
      ))}
      {ma.has_anfrage && taList.length === 0 ? (
        <span className="rounded-md border border-accent/35 bg-accent/10 px-2 py-0.5 text-xs font-medium text-accent">
          TA
        </span>
      ) : null}
      {ma.has_order && tkItems.length === 0 ? (
        <span className="rounded-md border border-status-green/35 bg-status-green/10 px-2 py-0.5 text-xs font-medium text-status-green">
          TK
        </span>
      ) : null}
    </div>
  );
}

function MaterialPreview({
  preview,
  total,
}: {
  preview: MaPositionPreview[];
  total: number;
}) {
  if (total === 0) {
    return <p className="py-2 text-sm text-label-tertiary">Keine Positionen.</p>;
  }
  if (preview.length === 0) {
    return (
      <p className="py-1 text-sm text-label-secondary">
        {total} Position(en) – Details öffnen
      </p>
    );
  }

  return (
    <ul className="ma-material-preview-lines">
          {preview.map((p, idx) => {
            const positionNote = (p.notes || "").trim();
            return (
              <li key={idx} className="ma-material-preview-line min-w-0">
                <p className="break-words text-sm text-label-secondary [overflow-wrap:anywhere]">
                  <span className="tabular-nums font-medium text-label">{p.menge ?? "—"}</span>
                  {p.einheit ? <span className="mx-1">{p.einheit}</span> : null}
                  <span>{p.text || "—"}</span>
                </p>
                {positionNote ? (
                  <p className="mt-0.5 whitespace-pre-wrap break-words text-xs text-label-tertiary [overflow-wrap:anywhere]">
                    {positionNote}
                  </p>
                ) : null}
              </li>
            );
          })}
          {total > preview.length ? (
            <li className="text-xs text-label-tertiary">
              + {total - preview.length} weitere …
            </li>
          ) : null}
    </ul>
  );
}

const TONE_RING: Record<LieferterminTone, string> = {
  none: "",
  normal: "ring-separator-opaque",
  soon: "ring-status-orange/35",
  today: "ring-accent/40",
  past: "ring-status-red/45",
};

function LieferterminBadge({
  liefertermin,
  kwLabel,
  fix,
  tone,
  urgent = false,
}: {
  liefertermin: string | null;
  kwLabel: string;
  fix: boolean;
  tone: LieferterminTone;
  urgent?: boolean;
}) {
  if (!liefertermin && !kwLabel) {
    return <span className="text-[11px] text-label-tertiary">kein Termin</span>;
  }

  const dateLabel = liefertermin ? fmtMaDateOnly(liefertermin) : "";

  if (fix && dateLabel) {
    return (
      <span
        className={clsx(
          "inline-flex max-w-full flex-col items-start gap-0.5 rounded-lg px-2.5 py-1 ring-2 ring-inset",
          urgent
            ? "border border-status-red/70 bg-status-red/20 ring-status-red/70 ma-liefertermin-badge--urgent"
            : "border border-status-orange/50 bg-status-orange/15 ring-status-orange/50",
        )}
      >
        <span className="inline-flex items-center gap-1.5">
          <span
            className={clsx(
              "text-[9px] font-bold uppercase tracking-wider",
              urgent ? "text-status-red" : "text-status-orange",
            )}
          >
            Fix
          </span>
          <span
            className={clsx(
              "text-sm font-semibold tabular-nums",
              urgent ? "text-status-red" : "text-label",
            )}
          >
            {dateLabel}
          </span>
        </span>
        {kwLabel ? (
          <span
            className={clsx(
              "text-[11px] font-medium",
              urgent ? "text-status-red/90" : "text-status-orange/90",
            )}
          >
            {kwLabel}
          </span>
        ) : null}
      </span>
    );
  }

  return (
    <span
      className={clsx(
        "inline-flex max-w-full flex-col items-start gap-0.5 rounded-lg px-2.5 py-1 ring-1 ring-inset bg-fill-tertiary",
        TONE_RING[tone],
        tone === "past" && "bg-status-red/10 text-status-red",
        tone === "today" && "bg-accent/10 text-accent",
        tone === "soon" && "bg-status-orange/10 text-status-orange",
      )}
    >
      {kwLabel ? (
        <span className="text-sm font-semibold tabular-nums">{kwLabel}</span>
      ) : null}
      {dateLabel ? (
        <span
          className={clsx(
            "tabular-nums",
            kwLabel ? "text-[11px] font-medium text-label-secondary" : "text-sm font-semibold",
          )}
        >
          {dateLabel}
        </span>
      ) : null}
    </span>
  );
}
